import type { ModuleFederationRuntimePlugin } from '@module-federation/enhanced/runtime';

const stripTrailingSlash = (s: string) => s?.replace(/\/+$/, '') || '';
const ensureTrailingSlash = (s: string) => (s ? (s.endsWith('/') ? s : `${s}/`) : '/');
const stripApiPath = (s: string) => s?.replace(/\/api\/?$/, '') || s;

const extractOriginFromUrl = (urlStr: string): string | null => {
  try {
    const url = new URL(urlStr);
    const pathParts = url.pathname.split('/');
    let basePath = '/';

    for (let i = 1; i < pathParts.length - 1; i++) {
      if (['js', 'static', 'css'].indexOf(pathParts[i]) !== -1) {
        basePath = `${pathParts.slice(0, i).join('/')}/`;
        break;
      }
    }
    if (basePath === '/') {
      basePath = `${pathParts.slice(0, -1).join('/')}/`;
    }
    return url.origin + (basePath || '/');
  } catch {
    return null;
  }
};

const resolvePublicPath = (remoteEntry?: string): string => {
  const win = window as Window &
    typeof globalThis & {
      __SSO_COMPUTED_PUBLIC_PATH__?: string;
      __FEDERATION__?: {
        ssoPublicPath?: string;
        __INSTANCES__?: Array<{
          options?: {
            remotes?: Array<{ name: string; entry?: string }>;
          };
        }>;
        moduleInfo?: {
          sso?: { entry?: string; remoteEntry?: string };
        };
      };
      __SSO_FRONTEND_URL__?: string;
      INITIAL_CONFIG?: { apiUrl?: string };
    };

  if (win.__SSO_COMPUTED_PUBLIC_PATH__) {
    return win.__SSO_COMPUTED_PUBLIC_PATH__;
  }
  let result: string | null = null;

  if (win.__FEDERATION__?.ssoPublicPath) {
    result = ensureTrailingSlash(stripTrailingSlash(win.__FEDERATION__.ssoPublicPath));
  }
  if (!result && typeof win.__SSO_FRONTEND_URL__ === 'string' && win.__SSO_FRONTEND_URL__.trim()) {
    result = ensureTrailingSlash(stripTrailingSlash(win.__SSO_FRONTEND_URL__.trim()));
  }
  if (!result && win.INITIAL_CONFIG?.apiUrl && typeof win.INITIAL_CONFIG.apiUrl === 'string') {
    result = ensureTrailingSlash(stripApiPath(stripTrailingSlash(win.INITIAL_CONFIG.apiUrl.trim())));
  }
  if (!result && remoteEntry) {
    result = extractOriginFromUrl(remoteEntry);
  }
  if (!result && win.__FEDERATION__?.__INSTANCES__) {
    const instances = win.__FEDERATION__.__INSTANCES__;
    for (const inst of instances) {
      if (inst?.options?.remotes) {
        for (const remote of inst.options.remotes) {
          if (remote?.name === 'sso' && remote.entry) {
            result = extractOriginFromUrl(remote.entry);
            if (result) {
              break;
            }
          }
        }
      }
      if (result) {
        break;
      }
    }
  }
  if (!result && win.__FEDERATION__?.moduleInfo?.sso) {
    const ssoInfo = win.__FEDERATION__.moduleInfo.sso;
    if (ssoInfo.entry) {
      result = extractOriginFromUrl(ssoInfo.entry);
    } else if (ssoInfo.remoteEntry) {
      result = extractOriginFromUrl(ssoInfo.remoteEntry);
    }
  }
  if (!result && typeof import.meta?.url === 'string') {
    result = extractOriginFromUrl(import.meta.url);
  }
  if (!result) {
    result = '/';
  }
  win.__SSO_COMPUTED_PUBLIC_PATH__ = result;
  return result;
};

const ssoRuntimePlugin = (): ModuleFederationRuntimePlugin => ({
  name: 'sso-public-path-plugin',

  beforeInit(args) {
    const { origin } = args;
    let remoteEntry: string | undefined;
    const remotes = origin.options?.remotes as Array<{ name?: string; entry?: string }> | undefined;

    if (remotes) {
      const ssoRemote = remotes.find((r) => r.name === 'sso' || r.entry?.includes('sso'));
      remoteEntry = ssoRemote?.entry;
    }
    resolvePublicPath(remoteEntry);

    return args;
  },

  beforeRequest(args) {
    const { id, options } = args;
    const remotes = options?.remotes as Array<{ name?: string; entry?: string }> | undefined;

    if (id?.includes('sso') && remotes) {
      const ssoRemote = remotes.find((r) => r.name === 'sso');
      if (ssoRemote?.entry) {
        resolvePublicPath(ssoRemote.entry);
      }
    }
    return args;
  },
});

export default ssoRuntimePlugin;
